/**
 * The one action of a case: one big button, "More", and the sentence that says what
 * stands between the case and a reply.
 *
 * `primaryActionOf` (case-action.ts) names the next step from the case state; the
 * button does it. "More" holds what is always available (Take, Take over, Release, the
 * status menu, Give feedback, Shortcuts) and the few extras of the state (Not them,
 * Pick one by one, No item, Add line, Approve price, Copy anyway, Edit text, Undo my
 * edits, Mark done and next, Copy again, Try again, Reopen). No primary is ever
 * disabled: "Nothing to do" opens the next case.
 *
 * The bar (ease round, 13 Oct 2026), in this order: the big button, "More", the sentence
 * of the blocker the button stands for (`captionOf`: a click goes to its control and
 * marks it), "Open points (n)" with the whole list, then "Details (n)". The sentence sits
 * in the bar, so it is pinned with the button: over the bar it was below the fold. When
 * the data of that blocker is not in F&O, the data gap block under the sentence says so
 * and offers the two statuses that close the case in one click.
 *
 * The keys of the case page are registered here: `j` / `k` next and previous case, `a`
 * accept the matched items, `c` confirm the suggested customer, `t` take, `d` mark done
 * and next, `r` copy the reply. On the page the bar is pinned to the viewport bottom
 * (`ActionBar sticky="bottom"`, a direct child of the work column); in the Outlook pane
 * it sits inline. The component returns a Fragment: the reply card, the alerts, then the
 * bar.
 *
 * The reply text sits in a card above the bar once a draft exists. "Copy the reply"
 * copies it with the subject and nothing else happens: nothing is sent from here.
 */

import { ChevronDown, Icon } from "@norican/icons";
import { ActionBar, Alert, Badge, Button, Menu, Typography } from "@norican/react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { Link } from "react-router";

import { CASE_SHORTCUTS, ShortcutHelpDialog } from "@/components/shortcut-help";
import {
  BlockerChecklist,
  DataGapBlock,
  isCaseDataGap,
} from "@/features/case-review/blocker-checklist";
import {
  type ActionFacts,
  type BlockerTarget,
  captionOf,
  closeHintOf,
  heldMarginLine,
  offersNotAnInquiryClose,
  openLines,
  type PrimaryAction as PrimaryActionModel,
  primaryActionOf,
} from "@/features/case-review/case-action";
import { type StripCustomer, type StripLines } from "@/features/case-review/case-summary-strip";
import { ReplyCard } from "@/features/case-review/reply-card";
import { type CaseFlow } from "@/features/case-review/use-case-flow";
import { type CaseWrites } from "@/features/case-review/use-case-writes";
import { useGiveFeedback } from "@/features/feedback";
import { TOUCH_QUERY, useMediaQuery } from "@/hooks/use-media-query";
import { useShortcutHandler } from "@/hooks/use-shortcuts";
import { type AnswerDraft, draftStaleText, isDraftStale, sendCaseActivity, useMe } from "@/lib/api";
import { hasCapability } from "@/lib/areas";
import { FEATURES } from "@/lib/features";
import { isOutOfScopeCase } from "@/lib/intents";
import { logger } from "@/lib/logger";
import { type QuoteCaseDetail } from "@/lib/mock";
import {
  ACTION_LABELS,
  CLOSE_OFFER_WORDS,
  PREPARED_BY,
  replyClipboardText,
  suggestedCustomerText,
} from "@/lib/sales-flow";
import { track } from "@/lib/usage";
import { useTrackOnce } from "@/lib/usage-react";

/** The text while a draft is being prepared. */
export const PREPARING_TEXT = "Preparing the reply. This can take up to a minute.";

export interface PrimaryActionProps {
  detail: QuoteCaseDetail;
  variant: "page" | "pane";
  lines: StripLines;
  customer: StripCustomer;
  /** The walk through the queue; null in the pane and on a shared link. */
  flow: CaseFlow | null;
  writes: CaseWrites;
  /** The newest reply draft, null without one. */
  draft: AnswerDraft | null;
  /**
   * False while the first read of the reply draft runs. The usage log waits for it, so
   * "the big button when the case was opened" is not "Prepare" on a case with a draft.
   */
  draftRead?: boolean;
  /** True while a draft is being prepared (by this page or the pane). */
  preparing: boolean;
  /** Why the last preparation failed, or null. */
  prepareError: string | null;
  /** Focus a row of the draft list ("Pick 2 items", "Pick one by one"). */
  onFocusLine: (lineId: string) => void;
  /** Open the customer search. */
  onFindCustomer: () => void;
  /** Opens the Details sheet at a section: the checks, or the order system. */
  onOpenSheet: (section: "checks" | "order-system") => void;
  /** Open "Add a missed line". */
  onAddLine: () => void;
  /**
   * A blocker sentence was clicked: go to its control (the customer search, a row of the
   * draft, the quantity dialog, "Add a missed line", the checks) and mark it.
   */
  onTarget: (target: BlockerTarget) => void;
  /** Opens "Set quantity for all lines". */
  onSetQuantityAll: () => void;
  /** Opens "Set price" for a line. */
  onSetPrice: (lineId: string) => void;
  /** What "Nothing to do" does without a queue walk (the pane opens the full case). */
  onNothing?: () => void;
  /** The end of the bar: "Details (n)", and "Open in I2Q" in the pane. */
  trailing?: ReactNode;
}

/** The ids of the extra items, with their labels. */
const EXTRA_LABEL = {
  try_again: ACTION_LABELS.tryAgain,
  reopen_as_inquiry: ACTION_LABELS.reopenAsInquiry,
  reopen: ACTION_LABELS.reopen,
  mark_done: ACTION_LABELS.markDone,
  next_case: ACTION_LABELS.nextCase,
  not_for_us: ACTION_LABELS.notForUs,
  not_them: ACTION_LABELS.notThem,
  pick_one_by_one: ACTION_LABELS.pickOneByOne,
  no_item: ACTION_LABELS.noItem,
  add_line: ACTION_LABELS.addLine,
  approve_price: ACTION_LABELS.approvePrice,
  waiting_customer: ACTION_LABELS.waitingCustomer,
  copy_anyway: ACTION_LABELS.copyAnyway,
  edit_text: ACTION_LABELS.editText,
  undo_edits: ACTION_LABELS.undoEdits,
  done_and_next: ACTION_LABELS.doneAndNext,
  copy_again: ACTION_LABELS.copyAgain,
} as const;

/** The selector of the sticky bar on the page (the `data-primary-action` of `ActionBar`). */
const BAR_SELECTOR = "[data-primary-action]";

/**
 * The bottom scroll padding of the page equals the height of the sticky bar while the bar
 * is on the page (hunt round 3, Q-27). A scroll into view, a keyboard focus or a find in
 * the page then stops above the bar. The padding goes when the bar goes.
 */
export function barScrollPadding(height: number): string {
  return height > 0 ? `${Math.ceil(height)}px` : "";
}

function useBarScrollPadding(on: boolean) {
  useEffect(() => {
    if (!on || typeof document === "undefined" || typeof ResizeObserver === "undefined") {
      return;
    }
    const bar = document.querySelector<HTMLElement>(BAR_SELECTOR);
    const root = document.documentElement;
    if (!bar) {
      return;
    }
    const apply = () => {
      root.style.scrollPaddingBottom = barScrollPadding(bar.getBoundingClientRect().height);
    };
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(bar);
    return () => {
      observer.disconnect();
      root.style.scrollPaddingBottom = "";
    };
  }, [on]);
}

/** The reply text state of one draft: keyed on the draft so a new draft starts fresh. */
function useReplyEdit(draft: AnswerDraft | null) {
  const original = draft?.body ?? "";
  const [state, setState] = useState<{ key: string; text: string; editing: boolean }>({
    key: original,
    text: original,
    editing: false,
  });
  // A new draft replaces the edit; the same draft keeps it.
  const current =
    state.key === original ? state : { key: original, text: original, editing: false };
  return {
    text: current.text,
    editing: current.editing,
    edited: current.text !== original,
    setText: (text: string) => setState({ key: original, text, editing: current.editing }),
    setEditing: (editing: boolean) => setState({ key: original, text: current.text, editing }),
    reset: () => setState({ key: original, text: original, editing: false }),
  };
}

export function PrimaryAction({
  detail,
  variant,
  lines,
  customer,
  flow,
  writes,
  draft,
  draftRead = true,
  preparing,
  prepareError,
  onFocusLine,
  onFindCustomer,
  onOpenSheet,
  onAddLine,
  onTarget,
  onSetQuantityAll,
  onSetPrice,
  onNothing,
  trailing,
}: PrimaryActionProps) {
  const { data: me } = useMe();
  const give = useGiveFeedback();
  const [copiedAt, setCopiedAt] = useState<string | null>(null);
  const [copied, setCopied] = useState<"yes" | "failed" | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  // A finger as the main pointer has no keys: no "Shortcuts" item.
  const touch = useMediaQuery(TOUCH_QUERY);
  const edit = useReplyEdit(draft);

  const hasText = Boolean(draft?.body?.trim());
  const draftStale = isDraftStale(draft);
  const mayApprove = hasCapability(me, "approve_price");
  const facts: ActionFacts = {
    me: me?.email ?? null,
    hasDraft: hasText,
    draftStale,
    copiedAt,
    mayApprove,
  };
  const action: PrimaryActionModel = primaryActionOf(detail, lines, customer, facts);
  // The account a one-click confirmation offers, in the words the button uses.
  const firstOption = customer.state === "suggested" ? customer.options[0] : undefined;
  const customerName =
    firstOption && customer.state === "suggested"
      ? suggestedCustomerText(firstOption, customer.options)
      : null;
  const sentence = captionOf(action, detail, { mayApprove, customerName });
  const heldLine = heldMarginLine(detail);
  const walking = FEATURES.caseFlow && flow?.fromQueue === true;
  const open = (detail.workStatus ?? "open") === "open";
  const rowStatus = detail.work?.status ?? "open";
  const closeHint = closeHintOf(detail);
  const firstOpen = openLines(detail.lines)[0] ?? null;
  // The usage log: what the big button said when the case was opened, once per case.
  useTrackOnce(
    "case.entry_state",
    {
      action: action.kind,
      lines: detail.lines.filter((line) => !line.fallback).length,
      from_queue: flow?.fromQueue === true,
    },
    draftRead ? detail.id : null,
    { caseId: detail.id },
  );

  const next = () => {
    if (flow) {
      flow.goNext();
    } else {
      onNothing?.();
    }
  };
  const doneAndNext = () =>
    writes.setStatus("done", () => (walking ? flow?.closedAndNext() : undefined));
  const copy = async () => {
    const text = edit.text.trim();
    if (!draft || text === "") {
      return;
    }
    try {
      await navigator.clipboard.writeText(
        FEATURES.copyReady ? replyClipboardText(draft.subject, edit.text) : edit.text,
      );
      setCopied("yes");
      setCopiedAt(new Date().toISOString());
      logger.debug("[case] reply copied", { case: detail.id });
      void sendCaseActivity(detail.id, {
        kind: "reply_copy",
        prepared_draft: draft.created_by === PREPARED_BY,
      });
      track(
        "case.reply_copy",
        { prepared: draft.created_by === PREPARED_BY, edited: edit.edited },
        { caseId: detail.id },
      );
    } catch (error) {
      logger.warn("[case] copy failed", error);
      setCopied("failed");
    }
  };

  // The keys of the case page, by what the state allows.
  useShortcutHandler("next", walking ? () => flow?.goNext() : null);
  useShortcutHandler("previous", walking && flow?.previous ? () => flow.goPrevious() : null);
  useShortcutHandler(
    "accept",
    action.kind === "accept" && !writes.busy ? () => writes.acceptMatched() : null,
  );
  useShortcutHandler(
    "confirmCustomer",
    action.kind === "confirm_customer" && action.account && !writes.busy
      ? () => writes.confirmCustomer(action.account ?? null)
      : null,
  );
  useShortcutHandler("take", writes.unassigned && !writes.busy ? () => writes.take() : null);
  useShortcutHandler(
    "doneAndNext",
    open && rowStatus !== "done" && !writes.busy ? doneAndNext : null,
  );
  useShortcutHandler("copyReply", hasText ? () => void copy() : null);

  // While its write runs, the big button is busy and the browser takes the focus off a
  // control that cannot be pressed. A person who works with the keys then had to walk the
  // page again for the next step (review of the ease round, 13 Oct 2026). The button
  // takes the focus back when the write is over and nothing else has it.
  const buttonRef = useRef<HTMLButtonElement>(null);
  const hadFocus = useRef(false);
  const loading = preparing || (writes.busy && action.kind !== "pick");
  useEffect(() => {
    if (loading || !hadFocus.current || typeof document === "undefined") {
      return;
    }
    const active = document.activeElement;
    if (!active || active === document.body) {
      buttonRef.current?.focus({ preventScroll: true });
    }
  }, [loading]);

  // Hunt round 3, Q-27: on the page the bar is sticky to the viewport bottom and covers
  // the last part of the screen. The page keeps a bottom scroll padding equal to the bar
  // height, so a marked line of the mail, a focused row and a found word scroll clear of
  // the bar (390 px: the bar is 113 px high).
  useBarScrollPadding(variant === "page");

  const run = () => {
    logger.debug("[case] primary action", { case: detail.id, kind: action.kind });
    hadFocus.current =
      typeof document !== "undefined" && document.activeElement === buttonRef.current;
    track("case.primary_action", { action: action.kind }, { caseId: detail.id });
    switch (action.kind) {
      case "reading":
        return;
      case "set_status":
        setMoreOpen(true);
        return;
      case "nothing":
        next();
        return;
      case "take_over":
        writes.takeOver();
        return;
      case "find_customer":
      case "choose_customer":
        onFindCustomer();
        return;
      case "confirm_customer":
        writes.confirmCustomer(action.account ?? null);
        return;
      case "accept":
        writes.acceptMatched();
        return;
      case "confirm_lines":
        // One write for every suggested line, as one click on "Confirm" in each row.
        writes.confirmSuggested(action.lineKeys ?? []);
        return;
      case "pick":
        if (action.lineId) {
          onTarget({ kind: "line", lineId: action.lineId });
        }
        return;
      case "approve_price":
        if (action.lineIndex !== undefined) {
          writes.approvePrice(action.lineIndex, true);
        }
        return;
      case "check":
        // A rule on one line: go to that row. A rule of the case: read it under "Checks".
        if (action.lineId) {
          onTarget({ kind: "line", lineId: action.lineId });
        } else {
          onOpenSheet("checks");
        }
        return;
      case "prepare":
        writes.prepareReply(false);
        return;
      case "prepare_again":
        writes.prepareReply(true);
        return;
      case "copy":
        void copy();
        return;
      case "done_and_next":
        doneAndNext();
        return;
    }
  };

  /** The usage log: an entry of "More" was used. `item` is the id in the code. */
  const moreItem = (
    item: keyof typeof EXTRA_LABEL | "take" | "take_over" | "release" | "shortcuts",
  ) => track("case.menu_item", { menu: "more", item }, { caseId: detail.id });

  const extra = (id: keyof typeof EXTRA_LABEL) => {
    logger.debug("[case] more", { case: detail.id, item: id });
    moreItem(id);
    switch (id) {
      case "try_again":
        onOpenSheet("checks");
        return;
      case "approve_price":
        // The same write as the big button and the row menu: the first held line.
        if (heldLine) {
          writes.approvePrice(heldLine.lineIndex, true);
        }
        return;
      case "reopen_as_inquiry":
        // The intent is I2Q's reading of the mail: the way to say it is wrong is feedback.
        give?.open({ surface: "case", caseId: detail.id });
        return;
      case "reopen":
        writes.setStatus("open");
        return;
      case "mark_done":
        writes.setStatus("done", () => (walking ? flow?.closedAndNext() : undefined));
        return;
      case "next_case":
        next();
        return;
      case "not_for_us":
        writes.setStatus("not_for_us", () => (walking ? flow?.closedAndNext() : undefined));
        return;
      case "not_them":
        onFindCustomer();
        return;
      case "pick_one_by_one": {
        const first = detail.lines.find(
          (line) => !line.fallback && line.requestAction !== "remove",
        );
        if (first) {
          onFocusLine(first.id);
        }
        return;
      }
      case "no_item":
        if (firstOpen?.lineKey) {
          writes.decideLine(
            detail.lines.findIndex((line) => line.id === firstOpen.id),
            firstOpen.lineKey,
            { kind: "no_item" },
          );
        }
        return;
      case "add_line":
        onAddLine();
        return;
      case "waiting_customer":
        writes.setStatus("waiting_customer");
        return;
      case "copy_anyway":
      case "copy_again":
        void copy();
        return;
      case "edit_text":
        edit.setEditing(!edit.editing);
        return;
      case "undo_edits":
        edit.reset();
        return;
      case "done_and_next":
        doneAndNext();
        return;
    }
  };

  const extraLabel = (id: keyof typeof EXTRA_LABEL): string => {
    if (id === "edit_text" && edit.editing) {
      return ACTION_LABELS.doneEditing;
    }
    if (id === "mark_done" && walking) {
      return ACTION_LABELS.doneAndNext;
    }
    return EXTRA_LABEL[id];
  };
  // A missed line can be added on any open inquiry, not only while a line is open, so
  // the action stays reachable on a case I2Q read no line from (F08).
  const offered: (keyof typeof EXTRA_LABEL)[] =
    open && !isOutOfScopeCase(detail) && !action.extras.includes("add_line")
      ? [...action.extras, "add_line"]
      : action.extras;
  // An extra that the state offers but that has nothing to act on is left out.
  const extras = offered.filter((id) => {
    if (id === "undo_edits") {
      return edit.edited;
    }
    if (id === "next_case") {
      return Boolean(flow) || Boolean(onNothing);
    }
    if (id === "no_item") {
      return Boolean(firstOpen?.lineKey);
    }
    if (id === "approve_price") {
      return mayApprove && Boolean(heldLine);
    }
    return true;
  });

  const caption = preparing ? PREPARING_TEXT : sentence.text;
  // While a reply is prepared the sentence is a wait, not a link.
  const target = preparing ? null : sentence.target;
  const note = preparing ? null : sentence.note;
  // The block speaks for the whole case ("I2Q cannot finish this case by itself"): one
  // line of many without an item or a price does not show it.
  const dataGap =
    !preparing && sentence.blocker && isCaseDataGap(sentence.blocker, detail)
      ? sentence.blocker
      : null;
  // Two sentences never run together: the first ends with a full stop before the hint.
  const closeHintText = closeHint
    ? `${/[.!?]$/.test(caption.trim()) ? "" : "."} Maybe no work needed: ${closeHint}.`
    : "";
  const showReply = Boolean(draft && hasText && !preparing);

  // A Fragment, not a wrapper: on the page the bar is sticky to the viewport bottom, and
  // a sticky element only moves within its parent's box. A 99 px wrapper around it kept
  // the bar below the fold (measured 12 Oct 2026), so the bar is a direct child of the
  // work column, which spans the page.
  return (
    <>
      {showReply && draft && (
        <ReplyCard
          draft={draft}
          text={edit.text}
          editing={edit.editing}
          onChange={(text) => {
            edit.setText(text);
            setCopied(null);
          }}
          copied={copied}
          stale={draftStale}
        />
      )}
      {draftStale && draft && !preparing && (
        <Alert tone="warning" title="Made before the case changed" data-reply-stale>
          {draftStaleText(draft)}
        </Alert>
      )}
      {prepareError && !preparing && (
        <Alert tone="warning" title="No draft" data-prepare-error>
          Could not prepare the reply: {prepareError}
        </Alert>
      )}
      {writes.error && (
        <Typography variant="caption" className="text-danger-fg" role="alert">
          {writes.error}
        </Typography>
      )}
      <ActionBar
        sticky={variant === "page" ? "bottom" : false}
        align="start"
        className="bg-bg flex flex-wrap items-center gap-2 border-t"
        aria-label="The one action"
        data-primary-action={action.kind}
      >
        {action.kind === "reading" ? (
          <Badge tone="info" variant="subtle" size="md">
            {action.label}
          </Badge>
        ) : (
          <Button
            ref={buttonRef}
            size="lg"
            tone="accent"
            variant={action.kind === "nothing" ? "outline" : "solid"}
            loading={loading}
            // The focus moved to another control: the button does not take it back.
            onBlur={(event) => {
              if (event.relatedTarget) {
                hadFocus.current = false;
              }
            }}
            // As high as "More" at least; a long label wraps and the button grows with it
            // (`h-auto` alone let it collapse to the height of its text, 18 px).
            className="h-auto min-h-(--norican-size-control-lg) max-w-full py-2 text-left whitespace-normal"
            onClick={run}
            data-primary-button
          >
            {action.label}
          </Button>
        )}
        {/* A notification or a newsletter (hunt round 2, decision 8): one click closes it
            as "not for us" with the note. I2Q never closes it by itself. */}
        {offersNotAnInquiryClose(detail) && (
          <Button
            size="lg"
            variant="outline"
            tone="neutral"
            disabled={writes.busy}
            onClick={() => {
              logger.debug("[case] close offer", { case: detail.id });
              track(
                "case.primary_action",
                { action: "close_not_an_inquiry" },
                { caseId: detail.id },
              );
              writes.setStatus(
                "not_for_us",
                () => (walking ? flow?.closedAndNext() : undefined),
                CLOSE_OFFER_WORDS.note,
              );
            }}
            data-close-offer
          >
            {CLOSE_OFFER_WORDS.button}
          </Button>
        )}
        <Menu.Root
          open={moreOpen}
          onOpenChange={(next) => {
            if (next) {
              track("case.menu_open", { menu: "more" }, { caseId: detail.id });
            }
            setMoreOpen(next);
          }}
        >
          <Menu.Trigger
            render={
              <Button
                size="lg"
                variant="ghost"
                tone="neutral"
                endIcon={<Icon icon={ChevronDown} size="xs" />}
              />
            }
          >
            {ACTION_LABELS.more}
          </Menu.Trigger>
          <Menu.Portal>
            <Menu.Positioner align="end" sideOffset={6}>
              <Menu.Popup>
                {writes.unassigned && (
                  <Menu.Item
                    disabled={writes.busy}
                    onClick={() => {
                      moreItem("take");
                      writes.take();
                    }}
                  >
                    {ACTION_LABELS.take}
                  </Menu.Item>
                )}
                {!writes.unassigned && !writes.mine && open && (
                  <Menu.Item
                    disabled={writes.busy}
                    onClick={() => {
                      moreItem("take_over");
                      writes.takeOver();
                    }}
                  >
                    {ACTION_LABELS.takeOver}
                  </Menu.Item>
                )}
                {writes.mine && (
                  <Menu.Item
                    disabled={writes.busy}
                    onClick={() => {
                      moreItem("release");
                      writes.release();
                    }}
                  >
                    {ACTION_LABELS.release}
                  </Menu.Item>
                )}
                <Menu.Separator />
                <Menu.Group>
                  <Menu.GroupLabel>{ACTION_LABELS.setStatus}</Menu.GroupLabel>
                  <Menu.Item
                    disabled={writes.busy || rowStatus === "done"}
                    onClick={() => extra("mark_done")}
                  >
                    {extraLabel("mark_done")}
                  </Menu.Item>
                  <Menu.Item
                    disabled={writes.busy || rowStatus === "not_for_us"}
                    onClick={() => extra("not_for_us")}
                  >
                    {ACTION_LABELS.notForUs}
                  </Menu.Item>
                  <Menu.Item
                    disabled={writes.busy || rowStatus === "waiting_customer"}
                    onClick={() => extra("waiting_customer")}
                  >
                    {ACTION_LABELS.waitingCustomer}
                  </Menu.Item>
                  {!open && (
                    <Menu.Item disabled={writes.busy} onClick={() => extra("reopen")}>
                      {ACTION_LABELS.reopen}
                    </Menu.Item>
                  )}
                </Menu.Group>
                {extras.filter(
                  (id) => !["mark_done", "not_for_us", "waiting_customer", "reopen"].includes(id),
                ).length > 0 && <Menu.Separator />}
                {extras
                  .filter(
                    (id) => !["mark_done", "not_for_us", "waiting_customer", "reopen"].includes(id),
                  )
                  .map((id) => (
                    <Menu.Item key={id} disabled={writes.busy} onClick={() => extra(id)}>
                      {extraLabel(id)}
                    </Menu.Item>
                  ))}
                <Menu.Separator />
                {give && (
                  <Menu.Item
                    onClick={() =>
                      give.open({
                        surface: variant === "pane" ? "addin" : "case",
                        caseId: detail.id,
                      })
                    }
                  >
                    {ACTION_LABELS.giveFeedback}
                  </Menu.Item>
                )}
                {FEATURES.shortcuts && !touch && (
                  <Menu.Item
                    onClick={() => {
                      moreItem("shortcuts");
                      setShortcutsOpen(true);
                    }}
                  >
                    {ACTION_LABELS.shortcuts}
                  </Menu.Item>
                )}
              </Menu.Popup>
            </Menu.Positioner>
          </Menu.Portal>
        </Menu.Root>
        {/* What stands between the case and a reply, pinned with the button. A sentence
            of a blocker is a link to its control; a caption of the state is plain text. */}
        <Typography
          as="span"
          variant="caption"
          tone="muted"
          className="min-w-0 flex-1 basis-48 break-words"
          data-action-caption
          data-close-hint={closeHint ? "" : undefined}
        >
          {action.captionCase ? (
            <>
              {caption.replace(action.captionCase, "")}
              <Link
                to={`/case/${encodeURIComponent(action.captionCase)}`}
                className="text-accent-fg-subtle font-semibold hover:underline"
              >
                {action.captionCase}
              </Link>
            </>
          ) : target ? (
            <Button
              size="sm"
              variant="ghost"
              tone="neutral"
              className="text-fg h-auto max-w-full justify-start px-1 py-1 text-left font-normal whitespace-normal"
              onClick={() => {
                logger.debug("[case] blocker sentence", {
                  case: detail.id,
                  kind: sentence.blocker?.kind,
                  target: target.kind,
                });
                onTarget(target);
              }}
              data-blocker-sentence
              data-blocker-kind={sentence.blocker?.kind}
            >
              <span className="min-w-0 break-words">{caption}</span>
            </Button>
          ) : (
            <span data-blocker-kind={sentence.blocker?.kind}>{caption}</span>
          )}
          {note ? ` ${note}` : ""}
          {closeHintText}
        </Typography>
        {!preparing && (
          <BlockerChecklist
            detail={detail}
            writes={writes}
            mayApprove={mayApprove}
            customerName={customerName}
            onTarget={onTarget}
            onSetQuantityAll={onSetQuantityAll}
            onSetPrice={onSetPrice}
            onClosed={() => (walking ? flow?.closedAndNext() : undefined)}
          />
        )}
        {trailing}
        {dataGap && (
          <div className="basis-full">
            <DataGapBlock
              blocker={dataGap}
              writes={writes}
              onClosed={() => (walking ? flow?.closedAndNext() : undefined)}
            />
          </div>
        )}
      </ActionBar>
      {shortcutsOpen && (
        <ShortcutHelpDialog open onOpenChange={setShortcutsOpen} rows={CASE_SHORTCUTS} />
      )}
    </>
  );
}
